import { fromZonedTime, toZonedTime, formatInTimeZone } from 'date-fns-tz'
import { detectUserTimezone } from './timezone'

export type TimezoneDisplayMode = 'local' | 'timezone' | 'utc'

/**
 * Get the effective timezone for display based on user preferences
 */
export function getEffectiveTimezone(
    userTimezone?: string,
    displayMode: TimezoneDisplayMode = 'timezone',
    detectedTimezone?: string
): string {
    switch (displayMode) {
        case 'local':
            return detectedTimezone || detectUserTimezone()
        case 'timezone':
            return userTimezone || 'UTC'
        case 'utc':
            return 'UTC'
        default:
            return userTimezone || 'UTC'
    }
}

/**
 * Format a date in the user's preferred timezone display mode
 */
export function formatDateInUserTimezone(
    date: Date | string,
    userTimezone?: string,
    displayMode: TimezoneDisplayMode = 'timezone',
    detectedTimezone?: string,
    formatString: string = 'PPP p',
    locale?: string
): string {
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date
        const effectiveTimezone = getEffectiveTimezone(userTimezone, displayMode, detectedTimezone)

        // If locale is provided, use Intl.DateTimeFormat for locale-aware formatting
        if (locale) {
            // Map formatString to Intl.DateTimeFormat options
            // For common format strings like 'PPP p', 'MMM d, yyyy h:mm a', etc.
            let intlOptions: Intl.DateTimeFormatOptions = {}
            
            if (formatString === 'PPP p') {
                // Full date with time (e.g., "October 23rd, 2025 at 6:16 PM")
                intlOptions = {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                }
            } else if (formatString === 'MMM d, yyyy h:mm a') {
                // Short format (e.g., "Oct 23, 2025 6:16 PM")
                intlOptions = {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                }
            } else if (formatString === 'PPPP p') {
                // Very long format with day of week
                intlOptions = {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                }
            } else {
                // Default: try to parse common date-fns patterns
                // For now, use a sensible default
                intlOptions = {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                }
            }

            return new Intl.DateTimeFormat(locale, {
                ...intlOptions,
                timeZone: effectiveTimezone
            }).format(dateObj)
        }

        // Fallback to date-fns-tz if no locale provided (for backward compatibility)
        return formatInTimeZone(dateObj, effectiveTimezone, formatString)
    } catch (error) {
        console.warn('Failed to format date in user timezone:', error)
        const dateObj = typeof date === 'string' ? new Date(date) : date
        return dateObj.toISOString()
    }
}

/**
 * Convert UTC date to user's effective timezone
 */
export function convertUTCToUserTimezone(
    utcDate: Date | string,
    userTimezone?: string,
    displayMode: TimezoneDisplayMode = 'timezone',
    detectedTimezone?: string
): Date {
    // Declared OUTSIDE the try so the catch can return it unconverted — see
    // the identical fix in timezone.ts.
    const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate
    try {
        const effectiveTimezone = getEffectiveTimezone(userTimezone, displayMode, detectedTimezone)

        return toZonedTime(dateObj, effectiveTimezone)
    } catch (error) {
        console.warn('Failed to convert UTC to user timezone:', error)
        return dateObj
    }
}

/**
 * Get timezone display name with current offset
 */
export function getTimezoneDisplayInfo(timezone: string): {
    name: string
    offset: string
    abbreviation: string
} {
    try {
        const now = new Date()
        const zonedDate = toZonedTime(now, timezone)

        // Get timezone abbreviation
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'short'
        })
        const parts = formatter.formatToParts(now)
        const abbreviation = parts.find(part => part.type === 'timeZoneName')?.value || ''

        // Get offset
        const offset = zonedDate.getTimezoneOffset()
        const offsetHours = Math.abs(offset) / 60
        const offsetMinutes = Math.abs(offset) % 60
        const offsetSign = offset <= 0 ? '+' : '-'
        const offsetString = `UTC${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`

        // Get timezone name
        const longFormatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'long'
        })
        const longParts = longFormatter.formatToParts(now)
        const timezoneName = longParts.find(part => part.type === 'timeZoneName')?.value || timezone

        return {
            name: timezoneName,
            offset: offsetString,
            abbreviation
        }
    } catch (error) {
        console.warn('Failed to get timezone display info:', error)
        return {
            name: timezone,
            offset: 'UTC+00:00',
            abbreviation: 'UTC'
        }
    }
}

/**
 * Get display mode label
 */
export function getDisplayModeLabel(
    displayMode: TimezoneDisplayMode,
    userTimezone?: string,
    detectedTimezone?: string
): string {
    switch (displayMode) {
        case 'local':
            return `Local (${detectedTimezone || 'Auto-detect'})`
        case 'timezone':
            return `User Timezone (${userTimezone || 'UTC'})`
        case 'utc':
            return 'UTC'
        default:
            return 'UTC'
    }
}
